// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useHistory, useLocation } from "react-router-dom";

import { toastError, toastWarning } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_FIELD_DESC_CLASS,
  BrokerAlertIcon,
  brokerAlertVariant,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import SettingCard from "./SettingCard";
import {
  completeCtraderOAuthCallback,
  putActiveTradeAccount,
  type CtraderOAuthAccount,
} from "../../../../services/brokerIntegrationApi";
import {
  clearCtraderOAuthPending,
  CTRADER_OAUTH_PROFILE_BROKER_TAB,
  CTRADER_OAUTH_PROFILE_SUCCESS,
  readCtraderOAuthAssetClasses,
  readCtraderOAuthEnvironment,
  readCtraderOAuthMdFetchConsent,
} from "../../../../utils/ctraderOpenApiSync";
import {
  CTRADER_OAUTH_POPUP_MESSAGE_TYPE,
  notifyCtraderOAuthOpener,
} from "../../../../utils/ctraderOAuthPopup";
import {
  ctraderDisplayFromOAuthAccount,
  formatCtraderConnectedSummary,
  formatCtraderOAuthPickerLabel,
} from "../../../../utils/ctraderAccountDisplay";

const BROKER_TAB_URL = CTRADER_OAUTH_PROFILE_BROKER_TAB;
const SUCCESS_RETURN_URL = CTRADER_OAUTH_PROFILE_SUCCESS;

const CtraderOAuthCallback = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const [phase, setPhase] = useState<"loading" | "pick_account" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [accounts, setAccounts] = useState<CtraderOAuthAccount[]>([]);
  const [pickedAccount, setPickedAccount] = useState("");
  const [isSubmittingPick, setIsSubmittingPick] = useState(false);
  const [oauthCode, setOauthCode] = useState("");

  const assetClasses = useMemo(() => readCtraderOAuthAssetClasses(), []);
  const connectEnvironment = useMemo(() => readCtraderOAuthEnvironment(), []);

  const goBackToBroker = () => {
    clearCtraderOAuthPending();
    if (
      notifyCtraderOAuthOpener({
        type: CTRADER_OAUTH_POPUP_MESSAGE_TYPE,
        status: "cancelled",
      })
    ) {
      return;
    }
    history.replace(BROKER_TAB_URL);
  };

  const resolveOAuthDisplay = (
    accountCode: string,
    result?: { trader_login?: number | null; executing_broker_name?: string | null }
  ) => {
    const fromList = accounts.find((a) => a.accountCode === accountCode);
    const merged: CtraderOAuthAccount = fromList ?? { accountCode };
    if (result?.trader_login != null && merged.traderLogin == null) {
      merged.traderLogin = result.trader_login;
      merged.login = result.trader_login;
    }
    if (result?.executing_broker_name && !merged.executingBrokerName) {
      merged.executingBrokerName = result.executing_broker_name;
    }
    return ctraderDisplayFromOAuthAccount(merged);
  };

  const finishConnect = async (
    accountCode: string,
    result?: { trader_login?: number | null; executing_broker_name?: string | null }
  ) => {
    clearCtraderOAuthPending();
    const code = accountCode.trim();
    if (code) {
      try {
        await putActiveTradeAccount("ctrader", code);
      } catch (activeError: unknown) {
        const msg =
          activeError instanceof Error
            ? activeError.message
            : "Could not set cTrader as your active trade account.";
        toastWarning("Connected, but active account not updated", {
          description: `${msg} Select the account on Broker Management if trades still show another broker.`,
          duration: 8000,
        });
      }
    }
    const display = code ? resolveOAuthDisplay(code, result) : null;
    const summary = code
      ? `${formatCtraderConnectedSummary(display ?? { ctid: code, platformLogin: null, executingBrokerName: null })}. You can sync trades on the Broker Management tab.`
      : "OAuth credentials saved.";

    if (
      notifyCtraderOAuthOpener({
        type: CTRADER_OAUTH_POPUP_MESSAGE_TYPE,
        status: "success",
        accountCode: code || undefined,
        message: summary,
      })
    ) {
      return;
    }

    toast.success("cTrader connected", {
      description: summary,
      duration: 5000,
    });
    history.replace(SUCCESS_RETURN_URL);
  };

  const exchangeCode = async (code: string, ctid?: string) => {
    const callbackPath = "/broker/ctrader/oauth/callback";
    return completeCtraderOAuthCallback(
      {
        code,
        traded_asset_classes: assetClasses,
        ...(connectEnvironment ? { environment: connectEnvironment } : {}),
        ...(ctid ? { ctid_trader_account_id: ctid } : {}),
        ...(readCtraderOAuthMdFetchConsent() ? { md_fetch_consent: true } : {}),
      },
      callbackPath
    );
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams(location.search);
      const code = (params.get("code") || "").trim();
      const oauthError = (params.get("error") || params.get("error_description") || "").trim();

      if (oauthError) {
        if (
          notifyCtraderOAuthOpener({
            type: CTRADER_OAUTH_POPUP_MESSAGE_TYPE,
            status: "error",
            message: oauthError,
          })
        ) {
          return;
        }
        setPhase("error");
        setErrorMessage(oauthError);
        return;
      }

      if (!code) {
        const missingCodeMessage =
          "Missing OAuth authorization code. Close this window and try connecting again.";
        if (
          notifyCtraderOAuthOpener({
            type: CTRADER_OAUTH_POPUP_MESSAGE_TYPE,
            status: "error",
            message: missingCodeMessage,
          })
        ) {
          return;
        }
        setPhase("error");
        setErrorMessage(missingCodeMessage);
        return;
      }

      setOauthCode(code);

      try {
        const result = await exchangeCode(code);
        if (cancelled) return;

        if (result.accounts.length > 1 && !result.account_code) {
          setAccounts(result.accounts);
          setPickedAccount(result.accounts[0]?.accountCode || "");
          setPhase("pick_account");
          return;
        }

        await finishConnect(result.account_code);
      } catch (error: any) {
        if (cancelled) return;
        const message = error?.message || "cTrader OAuth connect failed.";
        if (
          notifyCtraderOAuthOpener({
            type: CTRADER_OAUTH_POPUP_MESSAGE_TYPE,
            status: "error",
            message,
          })
        ) {
          return;
        }
        setPhase("error");
        setErrorMessage(message);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [location.search, assetClasses, connectEnvironment, history, toast]);

  const handleConfirmAccount = async () => {
    if (!pickedAccount) return;
    setIsSubmittingPick(true);
    try {
      let accountCode = pickedAccount;
      let oauthResult: { trader_login?: number | null; executing_broker_name?: string | null } | undefined;
      if (oauthCode) {
        try {
          const result = await exchangeCode(oauthCode, pickedAccount);
          accountCode = result.account_code || pickedAccount;
          oauthResult = result;
        } catch (exchangeError: any) {
          const msg = String(exchangeError?.message || "");
          if (!/invalid.*code|already.*used|expired/i.test(msg)) {
            throw exchangeError;
          }
          await putActiveTradeAccount("ctrader", pickedAccount);
        }
      }
      await finishConnect(accountCode, oauthResult);
    } catch (error: any) {
      toastError("Could not save account", {
        description: error?.message || "Try connecting again.",
        duration: 6000,
      });
    } finally {
      setIsSubmittingPick(false);
    }
  };

  return (
    <div className="mx-auto max-w-[640px] px-4 py-6 md:py-10">
      <SettingCard title="cTrader connection">
        {phase === "loading" ? (
          <div className="flex items-center gap-3 py-8">
            <Loader2 className="size-4 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
            <p className={BROKER_FIELD_DESC_CLASS}>Completing cTrader OAuth…</p>
          </div>
        ) : null}

        {phase === "error" ? (
          <div className="flex flex-col gap-4">
            <Alert variant={brokerAlertVariant("error")} className={BROKER_ALERT_CLASS}>
              <BrokerAlertIcon tone="error" />
              <AlertDescription className={BROKER_ALERT_DESC_CLASS}>{errorMessage}</AlertDescription>
            </Alert>
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              onClick={goBackToBroker}
            >
              Back to Broker Management
            </Button>
          </div>
        ) : null}

        {phase === "pick_account" ? (
          <div className="flex flex-col gap-4">
            <p className={BROKER_FIELD_DESC_CLASS}>
              Multiple cTrader accounts were found. Pick the account you trade on (login number).
              Tessera uses the ctid behind the scenes for sync.
            </p>
            <RadioGroup value={pickedAccount} onValueChange={setPickedAccount} className="gap-2">
              {accounts.map((account) => (
                <Label
                  key={account.accountCode}
                  htmlFor={`ctrader-account-${account.accountCode}`}
                  className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2 font-normal"
                >
                  <RadioGroupItem
                    value={account.accountCode}
                    id={`ctrader-account-${account.accountCode}`}
                  />
                  <span className="font-[family-name:var(--font-primary)] text-sm text-gray-700 dark:text-gray-100">
                    {formatCtraderOAuthPickerLabel(ctraderDisplayFromOAuthAccount(account))}
                  </span>
                </Label>
              ))}
            </RadioGroup>
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isSubmittingPick}
                aria-busy={isSubmittingPick || undefined}
                onClick={handleConfirmAccount}
              >
                {isSubmittingPick ? (
                  <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                ) : null}
                Use this account
              </Button>
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                onClick={goBackToBroker}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </SettingCard>
    </div>
  );
};

export default CtraderOAuthCallback;
